import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject, fromEvent, Subscription, timer } from 'rxjs';
import { Inject, Injectable, Renderer2, NgZone, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { loadLazyCssFile } from '../util';
import { HorizontalSlideDirective } from './horizontal-slide.directive';

/** 水平滑動管理器 */
@Injectable({
  providedIn: 'root'
})
export class HorizontalSlideManagerService {
  /** 放所有 slide 的陣列 */
  private slides$ = new BehaviorSubject<HorizontalSlideDirective[]>([]);
  private slideChangeSubc: Subscription;
  private wheelToSlideSubc: Subscription;
  /** 畫面是否已經渲染完成 */
  private isRendered = false;
  /** dom 渲染器，筆須由指令帶入 */
  private renderer: Renderer2;
  /** 當前顯示的 slide index */
  private _currentIndex = 0;
  /** 當前顯示的 slide index */
  get currentIndex() { return this._currentIndex; }
  /** 當前顯示的 slide */
  get currentSlide() { return this.slides$.value[this._currentIndex]; }
  /** 總共有幾個 slide */
  get totalSlideCount() { return this.slides$.value.length; }
  /** 滾動達到多少距離才換頁 */
  throttleDistance = 600;
  /** 累計滾動距離 */
  scrollOffsetY = 0;
  /** slide 後面還有內容 */
  get hasLeftView() { return this.document.body.offsetHeight > 0; }
  /** slide 後面還有內容且超過螢幕高度(會產生卷軸) */
  get isLeftViewOverflow() { return this.document.body.offsetHeight > window.innerHeight; }
  /** slides 的背景，為了讓底下的東西不要透出來 */
  private backfaceEl: HTMLElement;
  /** 是否正在動畫過渡中 */
  isAnimating = false;
  /** 是瀏覽器端渲染 */
  isBrowser: boolean;

  constructor(
    @Inject(DOCUMENT) protected document: Document,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * 將一個 slide 註冊到 slides 管理器
   * @param slide 一個 slide 的指令索引
   * @param renderer 渲染器，需要從 slide 帶進來
   */
  registSlide(slide: HorizontalSlideDirective, renderer: Renderer2) {
    if (!this.renderer) {
      // 第一個註冊進來的 slide 需要做些初始化動作
      this.renderer = renderer;
      if (this.isBrowser) {
        this.startListentSlidesChange();
      }
      // if (this.isLeftViewOverflow) {
        this.document.documentElement.setAttribute('style', 'overflow-y: hidden;');
      // }
      if (this.isBrowser) {
        loadLazyCssFile(this.renderer, slide.el, 'tmHorizontalSlideStyle', '/tm-horizontal-slide.css');
      }
    }

    const slides = this.slides$.value;
    slides.push(slide);
    this.slides$.next(slides.slice());
  }

  /** 重置整個 slides 管理器，當整個畫面銷毀時調用 */
  resetSlides() {
    this.stopListentSlidesChange();
    this.destroySlides();
    this.document.documentElement.setAttribute('style', '');
    this.document.documentElement.classList.remove('tm-hori-static-view');
    const styleLink = this.document.body.querySelector('#tmHorizontalSlideStyle') as any;
    this.document.body.removeChild(styleLink);
    this.slides$ = new BehaviorSubject<HorizontalSlideDirective[]>([]);
    delete this.renderer;
  }

  /** 開始監聽 slide 註冊變化，直到 slide 穩定後就渲染 slides */
  private startListentSlidesChange() {
    if (this.slideChangeSubc) { return; }

    this.slideChangeSubc = this.slides$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
    ).subscribe(slides => {
      console.log('slides && slides.length', slides && slides.length);
      if (slides && slides.length) {
        this.rendSlides(slides);
      } else {
        this.destroySlides();
      }
      // this.stopListentSlidesChange();
    });
  }

  /** 結束監聽 slide 註冊變化 */
  private stopListentSlidesChange() {
    if (this.slideChangeSubc) {
      this.slideChangeSubc.unsubscribe();
      delete this.slideChangeSubc;
    }
  }

  /** 渲染 slides */
  private rendSlides(slides: HorizontalSlideDirective[]) {
    if (this.isRendered) {
      this.destroySlides();
    }

    slides[0].active();
    slides.forEach((slide, index) => {
      slide.dispatchIndex(index);
      if (index > 0) {
        slide.setAfter();
      }
    });

    if (this.hasLeftView) {
      const backfaceEl = this.renderer.createElement('div');
      this.renderer.addClass(backfaceEl, 'tm-hori-slide-backface');
      this.renderer.insertBefore(slides[0].el.parentElement, backfaceEl, slides[0].el);
      this.backfaceEl = backfaceEl;
    }

    this.startListentWheelToSlide();

    this.isRendered = true;
  }

  /** slides 渲染的銷毀，只會還原畫面但不會對整個 slides 管理器做重置，用來在 slide 數量改變時重新更新 slide */
  private destroySlides() {
    if (!this.isRendered) { return; }

    this.isRendered = false;
    this._currentIndex = 0;
    this.scrollOffsetY = 0;
    this.stopListentWheelToSlide();
    if (this.backfaceEl) {
      this.renderer.removeChild(this.backfaceEl.parentElement, this.backfaceEl);
      delete this.backfaceEl;
    }
  }

  /** 開始監聽滑鼠滾輪事件來變更顯示的 slide */
  startListentWheelToSlide() {
    this.wheelToSlideSubc = fromEvent(this.document, 'wheel').subscribe((event: WheelEvent) => {
      // console.log(`on wheel currentIndex: ${this.currentIndex}, deltaY: ${event.deltaY}`);
      if (this.isAnimating) { return; }

      if (event.deltaY > 0) { // 下滾
        // 最後一張了
        if (this.currentIndex === this.totalSlideCount - 1) {
          if (this.hasLeftView && this.currentSlide.isActive) {
            this.scrollOffsetY += event.deltaY;
            if (this.scrollOffsetY >= (this.currentIndex + 1) * this.throttleDistance) {
              this.enterLeftView();
              this.scrollOffsetY = (this.currentIndex + 1) * this.throttleDistance;
            }
          }
          return;
        }
      } else { // 上滾
        // 第一張了
        if (this.currentIndex === 0) {
          if (this.scrollOffsetY > -event.deltaY) {
            this.scrollOffsetY += event.deltaY;
          }
          return;
        }
        // 最後一張的後面
        if (this.currentIndex === this.totalSlideCount - 1 && this.hasLeftView && !this.currentSlide.isActive) {
          if (window.scrollY === 0) {
            this.scrollOffsetY += event.deltaY;
            if (this.scrollOffsetY < this.currentIndex * this.throttleDistance) {
              this.leaveLeftView();
              this.scrollOffsetY = (this.currentIndex + 1) * this.throttleDistance;
            }
          }
          return;
        }
      }

      this.scrollOffsetY += event.deltaY;
      const nextPageOffset = (this.currentIndex + 1) * this.throttleDistance;
      const currentPageOffset = this.currentIndex * this.throttleDistance;
      // console.log(`scrollOffsetY change to: ${this.scrollOffsetY}`);
      if (this.scrollOffsetY >= nextPageOffset) {
        this.next();
        this.scrollOffsetY = nextPageOffset;
      } else if (this.scrollOffsetY < currentPageOffset) {
        this.prev();
        if (this.currentIndex === 0) {
          this.scrollOffsetY = 0;
        }
      }
    });
  }

  /** 結束監聽滑鼠滾輪事件e */
  stopListentWheelToSlide() {
    if (this.wheelToSlideSubc) {
      this.wheelToSlideSubc.unsubscribe();
      delete this.wheelToSlideSubc;
    }
  }

  /** 往後一個 slide */
  next() {
    // console.log(`next: ${this._currentIndex} --> ${this._currentIndex + 1}`);
    this.isAnimating = true;
    const prevSlide = this.currentSlide;
    prevSlide.setBefore();
    prevSlide.unActive();

    if (this.currentIndex === this.totalSlideCount - 1) {
      // if (this.isLeftViewOverflow) {
        // this.document.documentElement.setAttribute('style', '');
      // }
      this.isAnimating = false;
    } else {
      this._currentIndex += 1;
      this.currentSlide.active().subscribe(() => {
        this.isAnimating = false;
      });
    }
  }

  /** 往前一個 slide */
  prev() {
    // console.log(`prev: ${this._currentIndex} --> ${this._currentIndex - 1}`);
    this.isAnimating = true;
    const currentSlide = this.currentSlide;
    currentSlide.setAfter();
    currentSlide.unActive();

    this._currentIndex -= 1;
    const prevSlide = this.currentSlide;
    prevSlide.active().subscribe(() => {
      this.isAnimating = false;
    });
    this.isAnimating = false;
  }

  /** 進入 slides 後面剩下的畫面 */
  enterLeftView() {
    this.isAnimating = true;
    const prevSlide = this.currentSlide;
    prevSlide.setBefore();
    prevSlide.unActive();

    this.document.documentElement.classList.add('tm-hori-static-view');
    this.renderer.setStyle(this.backfaceEl, 'opacity', 0);
    timer(1000).subscribe(t => {
      this.renderer.setStyle(this.backfaceEl, 'display', 'none');
      this.isAnimating = false;
      if (this.isLeftViewOverflow) {
        this.document.documentElement.setAttribute('style', '');
      }
    });
  }

  /** 離開 slides 後面剩下的畫面(返回 slides) */
  leaveLeftView() {
    this.isAnimating = true;
    this.currentSlide.active().subscribe(() => {
      this.isAnimating = false;
    });

    this.document.documentElement.classList.remove('tm-hori-static-view');
    this.renderer.setStyle(this.backfaceEl, 'opacity', 1);
    this.renderer.setStyle(this.backfaceEl, 'display', 'block');

    if (this.isLeftViewOverflow) {
      this.document.documentElement.setAttribute('style', 'overflow-y: hidden;');
    }
  }
}
